import { useEffect, useState } from "react";
import {
  currentUser,
  updateUser,
  updatePassword,
} from "../../services/loginService";
import { useToast } from "../../provider/ToastContext";
import UpgradeModal from "./UpgradeModal";
import { useLocation } from "react-router-dom";

export default function UserProfile({ collapsed }) {
  const [name, setName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [plan, setPlan] = useState("Starter");
  const [email, setEmail] = useState("");
  const [userid, setUserid] = useState("");
  const { showToast } = useToast();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.upgradeSuccess) {
      showToast("Upgraded successfully!", "success");
      // Clear history state so it doesn't repeat on reload
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showToast]);

  const handleChange = () => {
    setShowPasswordForm(!showPasswordForm);
    setError("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSave = () => {
    if (newPassword !== confirmPassword) {
      setError("New password and confirm password do not match.");
      return;
    }

    let data = {};
    data.email = email;
    data.current_password = currentPassword;
    data.new_password = newPassword;
    updatePass(data);
    // Reset form after save
    setShowPasswordForm(false);
  };

  const updatePass = (data) => {
    updatePassword(data)
      .then((response) => {
        showToast("Updated Successfully", "success");
      })
      .catch((err) => {
        showToast("Something went wrong.", "error");
      });
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await currentUser();
        console.log(res);
        if (res.data.error) throw new Error("Failed to fetch user");
        setName(res.data.data.full_name);
        setEmail(res.data.data.email);
        setUserid(res.data.data.user_id);
      } catch (err) {
        console.error("Error fetching user:", err.message);
      }
    };
    fetchUser();
  }, []);

  return (
    <>
      <div
        className={`transition-all duration-300 ${
          collapsed ? "lg:ml-16" : "lg:ml-72"
        }`}
      >
        <div className="px-4 sm:px-0">
          <h3 className="text-base/7 font-semibold text-gray-900">
            User Information
          </h3>
          <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
            Personal details and subscription settings.
          </p>
        </div>

        <div className="mt-6 border-t border-gray-100">
          <dl className="divide-y divide-gray-100">
            {/* Full Name */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">Full name</dt>
              <dd className="mt-1 flex text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {isEditingName ? (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="grow border px-2 py-1 rounded-md text-gray-900"
                  />
                ) : (
                  <span className="grow">{name}</span>
                )}
                <span className="ml-4 shrink-0">
                  <button
                    onClick={async () => {
                      if (isEditingName) {
                        try {
                          const res = await updateUser(userid, {
                            full_name: name,
                          });

                          console.log("Name updated:", res.data);
                          showToast("Updated Successfully", "success");
                          // Optionally show a success toast here
                        } catch (err) {
                          console.error("Failed to update name", err);
                          showToast("Something went wrong.", "error");
                          // Optionally show an error toast here
                        } finally {
                          setIsEditingName(false);
                        }
                      } else {
                        setIsEditingName(true);
                      }
                    }}
                    className="rounded-md bg-white font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    {isEditingName ? "Save" : "Update"}
                  </button>
                </span>
              </dd>
            </div>

            {/* Email (readonly) */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">Email</dt>
              <dd className="mt-1 flex text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                <span className="grow">{email}</span>
              </dd>
            </div>

            {/* Current Plan */}
            {/* <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">
                Current Plan
              </dt>
              <dd className="mt-1 flex text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                <span className="grow">{plan}</span>
                <span className="ml-4 shrink-0">
                  <button>Upgrade Pro</button>
                </span>
              </dd>
            </div> */}

            <div>
              {/* Current Plan */}
              <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                <dt className="text-sm font-medium text-gray-900">
                  Current Plan
                </dt>
                <dd className="mt-1 flex text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  <span className="grow">{plan}</span>

                  <span className="ml-4 shrink-0">
                    <button
                      onClick={() => setIsUpgradeOpen(true)}
                      className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-500"
                    >
                      Upgrade Pro
                    </button>
                  </span>
                </dd>
              </div>

              {/* Upgrade Modal */}
              <UpgradeModal
                open={isUpgradeOpen}
                onClose={() => setIsUpgradeOpen(false)}
              />
            </div>

            {/* Change Password */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">
                Change Password
              </dt>
              <dd className="mt-1 flex flex-col gap-2 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                {showPasswordForm ? (
                  <div className="flex flex-col gap-2">
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Current password"
                      className="w-full rounded-md border px-3 py-1.5 text-sm text-gray-900 outline outline-1 outline-gray-300 focus:outline-indigo-600"
                    />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password"
                      className="w-full rounded-md border px-3 py-1.5 text-sm text-gray-900 outline outline-1 outline-gray-300 focus:outline-indigo-600"
                    />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="w-full rounded-md border px-3 py-1.5 text-sm text-gray-900 outline outline-1 outline-gray-300 focus:outline-indigo-600"
                    />
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={handleSave}
                        className="rounded-md bg-indigo-600 px-4 py-1.5 text-sm text-white hover:bg-indigo-500"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleChange}
                        className="rounded-md bg-gray-200 px-4 py-1.5 text-sm text-gray-800 hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="ml-auto shrink-0">
                    <button
                      onClick={handleChange}
                      className="rounded-md bg-white font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      Change Password
                    </button>
                  </span>
                )}
              </dd>
            </div>

            {/* Billing Details */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">
                Billing Details
              </dt>
              <dd className="mt-1 flex text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                <span className="grow"></span>
                <span className="ml-4 shrink-0">
                  <button
                    onClick={() => alert("Redirect to billing portal")}
                    className="rounded-md bg-white font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    View Here
                  </button>
                </span>
              </dd>
            </div>

            {/* Cancel Subscription */}
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
              <dt className="text-sm/6 font-medium text-gray-900">
                Cancel Subscription
              </dt>
              <dd className="mt-1 flex text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                <span className="grow"></span>
                <span className="ml-4 shrink-0">
                  <button
                    onClick={() => alert("Cancel subscription logic")}
                    className="rounded-md bg-white font-medium text-gray-600 hover:text-gray-800"
                  >
                    Cancel Subscription
                  </button>
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </>
  );
}
